// Arena — the live battle view + replay viewer rolled into one. Polls
// every 2s while the battle is live; freezes once finished.
//
// Spectators get a copy-deterrent UI (text-select disabled, right-click
// blocked, no download button). Contestants get the download link.
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sword, Trophy, Hourglass, Download, Lock, X, Share2 as Share } from "lucide-react";
import { getBattle, acceptChallenge, cancelChallenge, downloadBattle } from "./api";
import { toast } from "sonner";
import { useViewerSurface } from "@/lib/useViewerSurface";
import { useBattlesEnabled } from "./useBattlesEnabled";
import "./anti-copy.css";

const POLL_INTERVAL_MS = 2000;

// Safely extract a human-readable string from any Axios/FastAPI error.
// FastAPI 422 returns `detail` as an ARRAY of {type, loc, msg, ...} objects;
// shoving that straight into a toast crashes React (Feb 17, 2026 fix).
function errMsg(err, fallback = "Something went wrong.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return err?.message || fallback;
}

export default function Arena() {
  const { battleId } = useParams();
  const nav = useNavigate();
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const pollRef = useRef(null);
  const { user } = useViewerSurface();
  const { enabled: battlesEnabled, loading: flagLoading } = useBattlesEnabled();

  // Master-toggle hard gate.
  useEffect(() => {
    if (!flagLoading && battlesEnabled === false) {
      nav("/dashboard", { replace: true });
    }
  }, [flagLoading, battlesEnabled]);

  const fetchBattle = async () => {
    try {
      const b = await getBattle(battleId);
      setBattle(b);
      // countdown ticker
      if (b.state === "ready" && b.starts_at) {
        const ms = new Date(b.starts_at).getTime() - Date.now();
        setCountdown(Math.max(0, Math.floor(ms / 1000)));
      } else {
        setCountdown(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBattle(); }, [battleId]);

  // Poll only while the battle is moving
  useEffect(() => {
    if (!battle) return;
    const liveStates = ["pending_acceptance", "ready", "live", "scheduled"];
    if (!liveStates.includes(battle.state)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    // Slow poll for scheduled (every 30s); fast poll for live/ready/pending (every 2s)
    const interval = battle.state === "scheduled" ? 30_000 : POLL_INTERVAL_MS;
    pollRef.current = setInterval(fetchBattle, interval);
    return () => clearInterval(pollRef.current);
  }, [battle?.state]);

  // Block right-click + clipboard for spectators
  useEffect(() => {
    if (!battle?.is_spectator) return;
    const block = e => e.preventDefault();
    document.addEventListener("contextmenu", block);
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
    };
  }, [battle?.is_spectator]);

  // Feb 14, 2026 PM session #5 — Anti-copy event blockers for spectators.
  // Belt + suspenders alongside the CSS `.spectator-locked` rules. Blocks
  // copy + cut + contextmenu when the viewer isn't a contestant. Honest:
  // this is friction, not DRM — screenshots, dev-tools, mobile share are
  // all still possible. But it stops the casual right-click → copy path.
  useEffect(() => {
    if (!battle?.is_spectator) return;
    const stop = (e) => { e.preventDefault(); return false; };
    const events = ["copy", "cut", "contextmenu", "dragstart"];
    events.forEach((evt) => document.addEventListener(evt, stop));
    return () => events.forEach((evt) => document.removeEventListener(evt, stop));
  }, [battle?.is_spectator]);

  // Local countdown ticker
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Feb 14, 2026 — toast on per-round model fallback events.
  // Backend emits {type, round_index, from_model, to_model, ts} into
  // battle.model_events whenever a 503 forces Flash fallback OR when the
  // original model recovers afterward. We track the last seen length so we
  // only toast new events.
  const lastEventLenRef = useRef(0);
  useEffect(() => {
    const events = battle?.model_events || [];
    if (!Array.isArray(events) || events.length <= lastEventLenRef.current) return;
    // Only fire toasts for events we haven't seen yet
    for (let idx = lastEventLenRef.current; idx < events.length; idx++) {
      const evt = events[idx];
      if (!evt) continue;
      const roundLabel = `Round ${(evt.round_index ?? 0) + 1}`;
      if (evt.type === "flash_fallback") {
        toast(`${roundLabel}: ${evt.from_model || "Gemini Pro"} is busy — backing both sides up to Flash to keep the match alive.`, {
          icon: "⚡",
          duration: 6000,
        });
      } else if (evt.type === "primary_restored") {
        toast.success(`${roundLabel}: ${evt.to_model || "primary model"} reactivated.`, {
          duration: 5000,
        });
      }
    }
    lastEventLenRef.current = events.length;
  }, [battle?.model_events?.length]);

  const handleAccept = async (accept) => {
    try {
      const updated = await acceptChallenge(battleId, accept);
      setBattle(updated);
      toast.success(accept ? "Accepted. Countdown started." : "Challenge declined.");
    } catch (e) {
      toast.error(errMsg(e, "Could not respond."));
    }
  };

  const handleCancel = async () => {
    if (!confirm("Cancel this challenge?")) return;
    try {
      const updated = await cancelChallenge(battleId);
      setBattle(updated);
      toast.success("Challenge cancelled.");
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel."));
    }
  };

  // May 12, 2026 — spectator pacing. Anyone watching can advance a gated
  // round. The server is idempotent so multiple viewers clicking is fine —
  // first one wins, the rest are no-ops. Local optimistic state flips the
  // button to "starting…" immediately for UX snappiness.
  const [advancing, setAdvancing] = useState(false);
  // "Don't gate me" — per-viewer auto-advance. Stored in localStorage so it
  // sticks across reloads. When ON, this viewer's client auto-clicks
  // advance the moment any round gates. The server still respects the 5min
  // ceiling for other viewers.
  const [autoAdvance, setAutoAdvance] = useState(() => {
    try { return localStorage.getItem("battle-auto-advance") === "1"; } catch { return false; }
  });
  const toggleAutoAdvance = () => {
    const next = !autoAdvance;
    setAutoAdvance(next);
    try { localStorage.setItem("battle-auto-advance", next ? "1" : "0"); } catch { /* */ }
    if (next) toast.success("Auto-advance ON. Rounds will fire without waiting for you to click.");
    else toast.success("Auto-advance OFF. You'll have to click \"Start next round\" each time.");
  };
  const handleAdvance = async () => {
    if (advancing) return;
    setAdvancing(true);
    try {
      const { advanceRound } = await import("./api");
      await advanceRound(battleId);
      // Poll will pick up the state change on next tick (≤2s).
    } catch (e) {
      toast.error(errMsg(e, "Could not advance the round."));
    } finally {
      setAdvancing(false);
    }
  };
  // Auto-advance: when a round gates AND the user opted in, fire advance.
  useEffect(() => {
    if (!autoAdvance) return;
    if (!battle) return;
    if (battle.state !== "live") return;
    if (battle.gated_round_index == null) return;
    handleAdvance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battle?.gated_round_index, autoAdvance, battle?.state]);

  const handleDownload = async () => {
    if (!battle?.download_token) return;
    try {
      const data = await downloadBattle(battleId, battle.download_token);
      // Mac/Safari/iOS need the anchor in the DOM + a delayed revoke to
      // reliably trigger a download. Skip those two and the click silently
      // no-ops in some WebKit builds.
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `silo-wars-${battleId}.json`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so Safari has time to start the download.
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* */ }
        URL.revokeObjectURL(url);
      }, 500);
      toast.success("Transcript saved.");
    } catch (e) {
      toast.error("Download failed.");
    }
  };

  // Reminder modal state — only relevant when battle is `scheduled`.
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderEmail, setReminderEmail] = useState(user?.email || "");
  const [reminderMins, setReminderMins] = useState(15);
  const [reminderSaving, setReminderSaving] = useState(false);
  const submitReminder = async () => {
    if (!reminderEmail.trim()) return toast.error("Add an email.");
    setReminderSaving(true);
    try {
      await import("@/lib/api").then(m => m.api.post(`/battles/${battleId}/remind`, { email: reminderEmail.trim(), minutes_before: parseInt(reminderMins, 10) }));
      toast.success(`We'll email you ${reminderMins} minutes before the battle.`);
      setReminderOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Could not save reminder."));
    } finally {
      setReminderSaving(false);
    }
  };

  // Rematch state
  const [rematchOpen, setRematchOpen] = useState(false);
  const [rematchStrategy, setRematchStrategy] = useState("same"); // "same" | "ai" | "manual"
  const [rematchManual, setRematchManual] = useState(["", "", ""]);
  const [rematchSubmitting, setRematchSubmitting] = useState(false);
  // Feb 2026 (Randy pt-rematch-silo-swap) — same-owner rematches only.
  // Pre-seeded with the original battle's silos so a plain "Rematch" click
  // still re-runs the same pairing. If the user picks a different silo in
  // either dropdown, we send it as an override. Cross-owner rematches
  // hide these controls entirely (backend also ignores the fields there).
  const [rematchChallengerSiloId, setRematchChallengerSiloId] = useState("");
  const [rematchTargetSiloId, setRematchTargetSiloId] = useState("");
  const [rematchMyOwnedSilos, setRematchMyOwnedSilos] = useState([]);
  useEffect(() => {
    if (!rematchOpen) return;
    // Seed the swap dropdowns from the current battle regardless of same_owner
    // (harmless when hidden) so re-opening the modal restores the defaults.
    setRematchChallengerSiloId(battle?.challenger_silo?.id || "");
    setRematchTargetSiloId(battle?.target_silo?.id || "");
    // Only fetch the silo list when the swap UI will actually show up
    // (same-owner private rematch) — no need to pay the round-trip otherwise.
    if (!battle?.same_owner) return;
    (async () => {
      try {
        const m = await import("@/lib/api").then(x => x.api.get("/silos"));
        // Backend guards ownership on any override anyway; still filter to
        // the current user's owned silos so we don't offer collaborator
        // silos we know will be rejected with a 403.
        const owned = (m.data || []).filter(s => s.owner_user_id === user?.id);
        setRematchMyOwnedSilos(owned);
      } catch { /* ignore — dropdown falls back to the seed value */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rematchOpen]);
  const submitRematch = async () => {
    setRematchSubmitting(true);
    try {
      const cleanQs = rematchManual.map(q => q.trim()).filter(Boolean);
      if (rematchStrategy === "manual" && cleanQs.length === 0) {
        toast.error("Add at least one question.");
        setRematchSubmitting(false);
        return;
      }
      // Only forward the swap overrides on same-owner rematches, and only
      // when the user actually picked a different silo than the original.
      // Backend ignores these on cross-owner anyway, but keeping the wire
      // clean makes logs and tests easier to read.
      const swapPayload = {};
      if (battle?.same_owner) {
        if (rematchChallengerSiloId && rematchChallengerSiloId !== battle?.challenger_silo?.id) {
          swapPayload.challenger_silo_id_override = rematchChallengerSiloId;
        }
        if (rematchTargetSiloId && rematchTargetSiloId !== battle?.target_silo?.id) {
          swapPayload.target_silo_id_override = rematchTargetSiloId;
        }
      }
      const r = await import("@/lib/api").then(m => m.api.post(`/battles/${battleId}/rematch`, {
        question_strategy: rematchStrategy,
        manual_questions: rematchStrategy === "manual" ? cleanQs : null,
        ...swapPayload,
      }));
      toast.success(r.data?.same_owner ? "Rematch starting…" : "Rematch proposed — opponent has 60s to accept.");
      setRematchOpen(false);
      // Navigate to the new battle's arena
      if (r.data?.id) nav(`/wars/arena/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not propose rematch."));
    } finally {
      setRematchSubmitting(false);
    }
  };

  // Transfer state
  const [transferOpen, setTransferOpen] = useState(false);
  // Feb 17, 2026 — new fields:
  //  - transferName: required title for the resulting doc/slot/staging row.
  //                  Defaults to first 7 words of the first question.
  //  - transferRound: now a unified picker. "-1"/-2 sentinels carry the
  //    side ("full transcript left/right") in internal matches. In cross-
  //    creator battles, "-1" always means the side belonging to the user.
  const [transferName, setTransferName] = useState("");
  const [transferSide, setTransferSide] = useState("left");
  const [transferTarget, setTransferTarget] = useState("staging");
  const [transferRound, setTransferRound] = useState("-1");
  const [transferWithCommentary, setTransferWithCommentary] = useState(true);
  const [transferSiloId, setTransferSiloId] = useState("");
  const [mySilos, setMySilos] = useState([]);
  const [transferring, setTransferring] = useState(false);
  useEffect(() => {
    if (!transferOpen) return;
    (async () => {
      try {
        const m = await import("@/lib/api").then(x => x.api.get("/silos"));
        setMySilos(m.data || []);
        if ((m.data || []).length && !transferSiloId) setTransferSiloId(m.data[0].id);
      } catch { /* ignore */ }
    })();
    // Seed the default name from the first round's question (first 7 words).
    const q = battle?.rounds?.[0]?.question || "";
    const seed = q.replace(/[?!.,]/g, "").split(/\s+/).filter(Boolean).slice(0, 7).join(" ");
    setTransferName(seed || `Battle ${battleId.slice(0, 8)}`);
    // Default Side picker:
    //   - internal match → keep "left" (user can pick either via Round selector)
    //   - cross-creator   → force to user's own side
    if (!internalMatch && opponentSide) {
      setTransferSide(opponentSide === "left" ? "right" : "left");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferOpen]);
  const submitTransfer = async () => {
    setTransferring(true);
    try {
      const r = parseInt(transferRound, 10);
      // -1 = full transcript on `which_side`; -2 = full transcript opposite side.
      // 0..N = per-round answer on `which_side`.
      const isFull = r === -1 || r === -2;
      const body = {
        name: (transferName || "").trim() || `Battle ${battleId.slice(0, 8)}`,
        which_side: transferSide,
        target: transferTarget,
        target_silo_id: transferTarget === "silo" ? transferSiloId : null,
        round_index: r,
        include_commentary: isFull ? transferWithCommentary : false,
      };
      await import("@/lib/api").then(m => m.api.post(`/battles/${battleId}/transfer`, body));
      toast.success(
        transferTarget === "staging" ? "Sent to staging."
        : transferTarget.startsWith("slot") ? `Loaded into ${transferTarget}.`
        : "Added to silo library."
      );
      setTransferOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Transfer failed."));
    } finally {
      setTransferring(false);
    }
  };

  // Feb 17, 2026 — Share battle. Generates a copy-able replay URL gated to
  // creators. Non-creators see an upsell toast. Real share UX is just clipboard +
  // optional native share sheet (no platform-specific buttons needed yet).
  const [shareBusy, setShareBusy] = useState(false);
  const onShareBattle = async () => {
    if (!battle || battle.state !== "finished") {
      toast.error("Only finished battles can be shared.");
      return;
    }
    // Gate to creators. Use the canonical viewer surface — same field used
    // across the app for Operator-tier gates.
    const isCreator = !!(user?.is_paid_creator || user?.is_super_admin);
    if (!isCreator) {
      toast("Sharing battles is a Creator perk.", {
        description: "Upgrade to a paid Creator account to unlock shareable replay URLs.",
        action: { label: "See plans", onClick: () => nav("/account?tab=billing") },
      });
      return;
    }
    setShareBusy(true);
    try {
      const url = `${window.location.origin}/wars/arena/${battleId}`;
      // Prefer the native Web Share API on supported devices; fall back to
      // clipboard. Both are wrapped in try so a denied share doesn't surface
      // as an error toast.
      const challenger = battle.challenger_silo?.site_name || "Challenger";
      const target = battle.target_silo?.site_name || "Opponent";
      const shareData = {
        title: `${challenger} vs ${target} — Silo Wars`,
        text: `Watch this Silo Wars replay: ${challenger} vs ${target}`,
        url,
      };
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        try { await navigator.share(shareData); return; } catch { /* user cancelled */ }
      }
      const mod = await import("@/lib/io");
      const ok = await mod.copyText(url);
      toast[ok ? "success" : "error"](ok ? "Replay URL copied to clipboard." : "Couldn't copy. URL: " + url);
    } finally {
      setShareBusy(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B6A68]">Loading arena…</div>;
  if (!battle) return <div className="min-h-screen flex items-center justify-center text-[#6B6A68]">Battle not found.</div>;

  const { challenger_silo: l, target_silo: r, state, rounds, final_verdict, winner_silo_id, is_draw, elo_changes, is_contestant, is_spectator } = battle;

  // Pending direct challenge → show accept/decline if you're the target
  const youAreTarget = battle.target_silo && user && r && r.owner_id === user.id;
  const youAreChallenger = user && l && l.owner_id === user.id;
  // Feb 17, 2026 — internal match (you own both silos) means neither side is
  // your "opponent" — disables the opponent-locked anti-copy for those cases.
  const internalMatch = !!(youAreTarget && youAreChallenger);
  // The side OPPOSITE your account becomes the locked side for contestants.
  const opponentSide = is_contestant && !internalMatch
    ? (youAreChallenger ? "right" : (youAreTarget ? "left" : null))
    : null;

  return (
    <div className={`min-h-screen bg-[#0F0E0D] text-[#F5F2ED] ${is_spectator ? "spectator-locked" : ""}`} data-testid="arena-root">
      {/* Header */}
      <header className="border-b border-[#1C1B1A] bg-[#0F0E0D]/95 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => nav("/wars")} className="flex items-center gap-2 text-[#A89360] hover:text-[#F5F2ED]" data-testid="arena-back-btn">
            <ArrowLeft size={18} /> Battles
          </button>
          <div className="flex items-center gap-2">
            <Sword size={18} className="text-[#C16E5A]" />
            <span className="text-sm uppercase tracking-widest text-[#A89360]">Silo Wars</span>
            {battle.is_broadcast === false && (
              <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#A89360]/15 text-[#A89360] text-[10px] uppercase tracking-wider">
                <Lock size={10} /> Private
              </span>
            )}
            {battle.is_rehearsal && (
              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full bg-[#1C1B1A]/40 border border-[#A89360]/30 text-[#A89360] text-[10px] uppercase tracking-wider">
                Rehearsal · No Elo
              </span>
            )}
          </div>
          <StateBadge state={state} countdown={countdown} />
          {/* May 12, 2026 — live viewer counter. Updated by the 2s poll
              naturally. Only meaningful while the battle is live. */}
          {state === "live" && typeof battle.viewers_count === "number" && battle.viewers_count > 0 && (
            <div
              className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] inline-flex items-center gap-1.5 ml-2"
              data-testid="arena-viewers-count"
              title="Active viewers in the last 10 seconds"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C16E5A] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C16E5A]" />
              </span>
              {battle.viewers_count} watching
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Silo banners */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <SiloBanner silo={l} won={state === "finished" && !is_draw && winner_silo_id === l?.id} delta={elo_changes?.[l?.id]?.delta} side="left" />
          <SiloBanner silo={r} won={state === "finished" && !is_draw && winner_silo_id === r?.id} delta={elo_changes?.[r?.id]?.delta} side="right" />
        </div>

        {/* Topic / question source */}
        <div className="mb-6 text-center">
          <div className="text-xs uppercase tracking-widest text-[#A89360] mb-1">Topic</div>
          <div className="text-lg text-[#F5F2ED]">{battle.topic || "Custom question list"}</div>
          <div className="text-xs text-[#6B6A68] mt-1">
            {battle.same_owner && battle.target_provider && (battle.target_provider !== battle.silo_provider || battle.target_model !== battle.silo_model)
              ? <>L: {battle.silo_provider}/{battle.silo_model} · R: {battle.target_provider}/{battle.target_model} · Referee: {battle.referee_provider}/{battle.referee_model}</>
              : <>Both silos on {battle.silo_provider}/{battle.silo_model} · Referee: {battle.referee_provider}/{battle.referee_model}</>
            }
          </div>
        </div>

        {/* Pending accept */}
        {state === "pending_acceptance" && (
          <div className={`border rounded-lg p-6 text-center mb-6 ${battle.is_broadcast ? "bg-[#1C1B1A] border-[#C16E5A]/40" : "bg-[#1C1B1A] border-[#A89360]/30"}`}>
            <div className="mb-3 flex items-center justify-center gap-2">
              <span className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${battle.is_broadcast ? "text-[#C16E5A]" : "text-[#A89360]"}`}>
                {battle.is_broadcast ? "● LIVE BATTLE REQUEST" : "🔒 PRIVATE BATTLE REQUEST"}
              </span>
            </div>
            {battle.scheduled_for && (
              <div className="mb-3 text-xs text-[#A89360]">
                Scheduled for <strong>{new Date(battle.scheduled_for).toLocaleString()}</strong>
              </div>
            )}
            {youAreTarget ? (
              <>
                <p className="text-[#F5F2ED] mb-4">{battle.note ? <em>"{battle.note}"</em> : "A creator wants to fight your silo."}</p>
                <p className="text-[11px] text-[#6B6A68] mb-4">
                  {battle.is_broadcast
                    ? "Accepting puts this match on the public arena — anyone can watch."
                    : "This is a private match. Only you and the challenger will see it."}
                </p>
                <div className="flex gap-2 justify-center">
                  <button onClick={() => handleAccept(true)} className="px-5 py-2 bg-[#C16E5A] text-white rounded-md hover:opacity-90" data-testid="arena-accept-btn">Accept</button>
                  <button onClick={() => handleAccept(false)} className="px-5 py-2 bg-[#1C1B1A] border border-[#6B6A68] text-[#F5F2ED] rounded-md hover:bg-[#2C2B2A]" data-testid="arena-decline-btn">Decline</button>
                </div>
              </>
            ) : youAreChallenger ? (
              <>
                <p className="text-[#A89360] mb-4">Waiting on opponent to accept…</p>
                <button onClick={handleCancel} className="px-4 py-2 text-sm text-[#C16E5A] hover:underline" data-testid="arena-cancel-btn">Cancel challenge</button>
              </>
            ) : (
              <p className="text-[#6B6A68]">Awaiting opponent acceptance.</p>
            )}
          </div>
        )}

        {/* Scheduled — battle is parked until its scheduled time */}
        {state === "scheduled" && (
          <div className="bg-[#1C1B1A] border border-[#A89360]/40 rounded-lg p-8 text-center mb-6">
            <Hourglass size={28} className="text-[#A89360] mx-auto mb-2" />
            <div className="text-xs uppercase tracking-[0.2em] text-[#A89360] mb-2">
              {battle.is_broadcast ? "● LIVE BATTLE — SCHEDULED" : "🔒 PRIVATE BATTLE — SCHEDULED"}
            </div>
            <div className="text-2xl font-light text-[#F5F2ED] mb-1">
              {battle.scheduled_for ? new Date(battle.scheduled_for).toLocaleString() : "TBD"}
            </div>
            <p className="text-xs text-[#6B6A68] mt-2">
              The arena will open with a 60-second countdown right before the scheduled time.
            </p>
            <button
              onClick={() => setReminderOpen(true)}
              className="mt-4 px-4 py-2 bg-[#A89360] text-[#1C1B1A] rounded-md hover:opacity-90 text-sm"
              data-testid="arena-remind-btn"
            >
              Remind me by email
            </button>
          </div>
        )}

        {/* Reminder modal */}
        {reminderOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setReminderOpen(false)} data-testid="arena-reminder-modal">
            <div className="bg-white text-[#1C1B1A] rounded-lg shadow-2xl max-w-sm w-full p-5" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-lg mb-1">Remind me</h3>
              <p className="text-xs text-[#6B6A68] mb-4">We'll email you so you don't miss the live arena.</p>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B6A68] mb-1">Email</label>
              <input
                type="email"
                value={reminderEmail}
                onChange={e => setReminderEmail(e.target.value)}
                className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm mb-3"
                data-testid="arena-reminder-email"
              />
              <label className="block text-[11px] uppercase tracking-wider text-[#6B6A68] mb-1">Minutes before</label>
              <select
                value={reminderMins}
                onChange={e => setReminderMins(e.target.value)}
                className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm mb-4"
                data-testid="arena-reminder-mins"
              >
                {[5, 10, 15, 30, 60, 120].map(m => <option key={m} value={m}>{m} min before</option>)}
              </select>
              <div className="flex justify-end gap-2">
                <button onClick={() => setReminderOpen(false)} className="px-3 py-1.5 text-sm text-[#6B6A68]" data-testid="arena-reminder-cancel">Cancel</button>
                <button
                  onClick={submitReminder}
                  disabled={reminderSaving}
                  className="px-4 py-1.5 bg-[#1C1B1A] text-white text-sm rounded-md disabled:opacity-50"
                  data-testid="arena-reminder-submit"
                >
                  {reminderSaving ? "Saving…" : "Set reminder"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rematch modal */}
        {rematchOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setRematchOpen(false)} data-testid="arena-rematch-modal">
            <div className="bg-white text-[#1C1B1A] rounded-lg shadow-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-lg mb-1">Rematch?</h3>
              <p className="text-xs text-[#6B6A68] mb-4">
                {battle.same_owner
                  ? "Personal rematch — starts immediately."
                  : "If you change the questions, your opponent has 60 seconds to accept on their end."}
              </p>
              {/* Feb 2026 (Randy pt-rematch-silo-swap) — private (same-owner)
                  rematches only. Cross-owner rematches don't show these
                  dropdowns; the backend also ignores the fields there so a
                  crafted request can't swap in someone else's silo. Both
                  dropdowns default to the original pairing, so a plain
                  "Propose rematch" click still re-runs the same match. */}
              {battle.same_owner && (
                <div className="mb-4 rounded-md border border-[#E7E4DE] bg-[#FDFCF8] p-3" data-testid="arena-rematch-swap-panel">
                  <div className="text-xs font-medium text-[#1C1B1A] mb-2">Silos (optional swap)</div>
                  <div className="space-y-2">
                    <label className="block">
                      <span className="text-[11px] text-[#6B6A68] block mb-1">Left silo</span>
                      <select
                        value={rematchChallengerSiloId}
                        onChange={(e) => setRematchChallengerSiloId(e.target.value)}
                        className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm bg-white"
                        data-testid="arena-rematch-swap-challenger"
                      >
                        {rematchMyOwnedSilos.length === 0 && rematchChallengerSiloId && (
                          <option value={rematchChallengerSiloId}>
                            {battle?.challenger_silo?.name || "Original silo"}
                          </option>
                        )}
                        {rematchMyOwnedSilos.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-[11px] text-[#6B6A68] block mb-1">Right silo</span>
                      <select
                        value={rematchTargetSiloId}
                        onChange={(e) => setRematchTargetSiloId(e.target.value)}
                        className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm bg-white"
                        data-testid="arena-rematch-swap-target"
                      >
                        {rematchMyOwnedSilos.length === 0 && rematchTargetSiloId && (
                          <option value={rematchTargetSiloId}>
                            {battle?.target_silo?.name || "Original silo"}
                          </option>
                        )}
                        {rematchMyOwnedSilos.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="text-[11px] text-[#6B6A68] mt-2">
                    Leave either dropdown on the original silo to keep that side unchanged.
                  </div>
                </div>
              )}
              <div className="space-y-2 mb-4">
                {[
                  { v: "same", label: "Same questions", hint: "Re-runs the original prompts. No acceptance needed." },
                  { v: "ai", label: "Referee writes new ones", hint: "Both sides need to accept within 15s." },
                  { v: "manual", label: "I'll add 3 new questions", hint: "Both sides need to accept within 15s." },
                ].map(opt => (
                  <label key={opt.v} className={`block p-3 rounded-md border cursor-pointer ${rematchStrategy === opt.v ? "border-[#C16E5A] bg-[#C16E5A]/5" : "border-[#D8D6D2]"}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <input
                        type="radio"
                        checked={rematchStrategy === opt.v}
                        onChange={() => setRematchStrategy(opt.v)}
                        data-testid={`arena-rematch-${opt.v}`}
                      />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </div>
                    <div className="text-[11px] text-[#6B6A68] pl-5">{opt.hint}</div>
                  </label>
                ))}
              </div>
              {rematchStrategy === "manual" && (
                <div className="space-y-2 mb-4">
                  {rematchManual.map((q, i) => (
                    <input
                      key={i}
                      type="text"
                      placeholder={`Question ${i + 1}`}
                      value={q}
                      onChange={e => {
                        const next = [...rematchManual];
                        next[i] = e.target.value;
                        setRematchManual(next);
                      }}
                      className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                      data-testid={`arena-rematch-q-${i}`}
                    />
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setRematchOpen(false)} className="px-3 py-1.5 text-sm text-[#6B6A68]" data-testid="arena-rematch-cancel">Cancel</button>
                <button
                  onClick={submitRematch}
                  disabled={rematchSubmitting}
                  className="px-4 py-1.5 bg-[#C16E5A] text-white text-sm rounded-md disabled:opacity-50"
                  data-testid="arena-rematch-submit"
                >
                  {rematchSubmitting ? "Sending…" : "Propose rematch"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Transfer-output modal — redesigned Feb 17, 2026 */}
        {transferOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-4" onClick={() => setTransferOpen(false)} data-testid="arena-transfer-modal">
            <div className="bg-white text-[#1C1B1A] rounded-lg shadow-2xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <h3 className="font-semibold text-lg mb-3">Transfer output</h3>
              {/* Required name. Default: first 7 words of the first question. */}
              <Field label="Name (required)">
                <input
                  value={transferName}
                  onChange={(e) => setTransferName(e.target.value)}
                  placeholder="e.g. Two faith systems on free will"
                  className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm"
                  data-testid="arena-transfer-name"
                  maxLength={120}
                />
              </Field>
              {/* Which round? — now also exposes Full transcript left/right
                  for internal matches per Feb 17, 2026 spec. In cross-creator
                  matches, only Full transcript (your side) shows because the
                  backend (and the UX) blocks cross-account content theft. */}
              <Field label="Which round?">
                <select value={transferRound} onChange={e => {
                  const v = e.target.value;
                  setTransferRound(v);
                  // -1 / -2 sentinels map to the corresponding side for clarity.
                  if (v === "-1") setTransferSide("left");
                  else if (v === "-2") setTransferSide("right");
                }} className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm" data-testid="arena-transfer-round">
                  {internalMatch ? (
                    <>
                      <option value="-1">Full transcript (left side · {battle.challenger_silo?.site_name || "Challenger"})</option>
                      <option value="-2">Full transcript (right side · {battle.target_silo?.site_name || "Opponent"})</option>
                    </>
                  ) : (
                    <option value="-1">Full transcript (your side)</option>
                  )}
                  {(battle.rounds || []).map((_, i) => <option key={i} value={i}>Round {i + 1}</option>)}
                </select>
              </Field>
              {/* Commentary toggle — applies only to full-transcript exports.
                  Feb 17, 2026 — "Off = Questions + bare answers only" so the
                  questions always come through even without verdicts. */}
              {(transferRound === "-1" || transferRound === "-2") && (
                <div className="-mt-2 mb-3 flex items-start gap-2 p-2.5 bg-[#FDF1ED]/50 border border-[#C16E5A]/30 rounded-md">
                  <input
                    type="checkbox"
                    id="arena-transfer-commentary"
                    checked={transferWithCommentary}
                    onChange={(e) => setTransferWithCommentary(e.target.checked)}
                    className="mt-0.5 accent-[#C16E5A] cursor-pointer"
                    data-testid="arena-transfer-commentary"
                  />
                  <label htmlFor="arena-transfer-commentary" className="text-xs text-[#1C1B1A] cursor-pointer leading-relaxed">
                    <strong>Include referee commentary</strong> — per-round verdict + this side's pull-quote + final verdict. Off = Questions + bare answers only.
                  </label>
                </div>
              )}
              <Field label="Send to">
                <select value={transferTarget} onChange={e => setTransferTarget(e.target.value)} className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm" data-testid="arena-transfer-target">
                  <option value="staging">Staging area</option>
                  <option value="slot1">Workbench · Slot 1</option>
                  <option value="slot2">Workbench · Slot 2</option>
                  <option value="slot3">Workbench · Slot 3</option>
                  <option value="silo">Silo library (RAG)</option>
                </select>
              </Field>
              {transferTarget === "silo" && (
                <Field label="Destination silo">
                  <select value={transferSiloId} onChange={e => setTransferSiloId(e.target.value)} className="w-full border border-[#D8D6D2] rounded-md p-2 text-sm" data-testid="arena-transfer-silo">
                    {mySilos.map(s => <option key={s.id} value={s.id}>{s.name || s.slug}</option>)}
                  </select>
                </Field>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setTransferOpen(false)} className="px-3 py-1.5 text-sm text-[#6B6A68]" data-testid="arena-transfer-cancel">Cancel</button>
                <button
                  onClick={submitTransfer}
                  disabled={transferring || !(transferName || "").trim()}
                  className="px-4 py-1.5 bg-[#1C1B1A] text-white text-sm rounded-md disabled:opacity-50"
                  data-testid="arena-transfer-submit"
                >
                  {transferring ? "Sending…" : "Transfer"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Countdown */}
        {state === "ready" && (
          <div className="bg-[#1C1B1A] border border-[#A89360]/40 rounded-lg p-8 text-center mb-6">
            <Hourglass size={28} className="text-[#A89360] mx-auto mb-2 animate-pulse" />
            <div className="text-4xl font-light text-[#F5F2ED] mb-1">{countdown !== null ? `${countdown}s` : "—"}</div>
            <div className="text-xs uppercase tracking-widest text-[#A89360]">Battle starting</div>
          </div>
        )}

        {/* Rounds — show completed rounds in live, finished, AND aborted
            states (May 12, 2026). Previously aborted battles wiped the
            whole transcript, losing rounds 1+2 if round 3 failed.
            Live state ALWAYS shows the live indicator (even with 0 rounds
            yet) so users see a "thinking…" pulse from the moment the
            countdown ends. */}
        {state === "live" && (
          <div className="space-y-4">
            {(rounds || []).map((round, i) => (
              <RoundCard key={i} round={round} index={i} l={l} r={r} opponentSide={opponentSide} />
            ))}
            {battle.gated_round_index != null ? (
              <SpectatorGateCard
                battle={battle}
                onAdvance={handleAdvance}
                advancing={advancing}
                autoAdvance={autoAdvance}
                onToggleAutoAdvance={toggleAutoAdvance}
              />
            ) : battle.current_round_state === "retrying" ? (
              <RetryingCard reason={battle.current_round_retry_reason} />
            ) : (
              <LiveRoundIndicator battle={battle} l={l} r={r} />
            )}
          </div>
        )}
        {state === "finished" && (rounds || []).length > 0 && (
          <div className="space-y-4">
            {(rounds || []).map((round, i) => (
              <RoundCard key={i} round={round} index={i} l={l} r={r} opponentSide={opponentSide} />
            ))}
          </div>
        )}
        {state === "aborted" && (rounds || []).length > 0 && (
          <div className="space-y-4">
            {(rounds || []).map((round, i) => (
              <RoundCard key={i} round={round} index={i} l={l} r={r} opponentSide={opponentSide} />
            ))}
          </div>
        )}

        {/* Aborted / cancelled */}
        {state === "aborted" && (
          <div className={`bg-[#C16E5A]/10 border border-[#C16E5A]/40 rounded-lg p-6 text-center ${(rounds || []).length > 0 ? "mt-6" : ""}`}>
            <p className="text-[#C16E5A] mb-1">Battle aborted.</p>
            <p className="text-xs text-[#6B6A68]">{battle.abort_reason || "An error stopped the match. No Elo change."}</p>
            {(rounds || []).length > 0 && (
              <p className="text-[10px] text-[#6B6A68] mt-2 italic">
                {(rounds || []).length} completed round{(rounds || []).length === 1 ? "" : "s"} above — content is preserved even though Elo didn't change.
              </p>
            )}
          </div>
        )}
        {state === "declined" && <div className="text-center text-[#6B6A68] py-12">Challenge was declined.</div>}
        {state === "cancelled" && <div className="text-center text-[#6B6A68] py-12">Challenge was cancelled.</div>}

        {/* Final verdict */}
        {state === "finished" && final_verdict && (
          <div className="mt-8 bg-gradient-to-br from-[#1C1B1A] to-[#2C2B2A] border border-[#A89360]/30 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={18} className="text-[#A89360]" />
              <span className="text-xs uppercase tracking-widest text-[#A89360]">Final verdict</span>
            </div>
            <p className="text-[#F5F2ED] whitespace-pre-line leading-relaxed arena-text">{final_verdict}</p>

            {is_contestant && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#A89360] text-[#1C1B1A] rounded-md hover:opacity-90 text-sm"
                  data-testid="arena-download-btn"
                >
                  <Download size={14} /> Download transcript
                </button>
                <button
                  onClick={() => setTransferOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C1B1A]/10 hover:bg-[#1C1B1A]/20 text-[#F5F2ED] rounded-md text-sm border border-[#A89360]/30"
                  data-testid="arena-transfer-btn"
                >
                  Transfer output…
                </button>
                <button
                  onClick={() => setRematchOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#C16E5A] text-white rounded-md hover:opacity-90 text-sm"
                  data-testid="arena-rematch-btn"
                >
                  ⚔ Rematch
                </button>
                {/* Feb 17, 2026 — Share battle. Creator-gated; non-creators
                    get an upsell toast pointing at billing. */}
                <button
                  onClick={onShareBattle}
                  disabled={shareBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C1B1A]/10 hover:bg-[#1C1B1A]/20 text-[#F5F2ED] rounded-md text-sm border border-[#A89360]/30 disabled:opacity-50"
                  data-testid="arena-share-btn"
                  title="Share a replay URL of this battle"
                >
                  <Share size={14} /> {shareBusy ? "…" : "Share battle"}
                </button>
              </div>
            )}
            {/* Share button also visible to spectators (creator-gated) so a
                Creator who's watching, not contesting, can still share. */}
            {is_spectator && state === "finished" && (
              <div className="mt-4">
                <button
                  onClick={onShareBattle}
                  disabled={shareBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C1B1A]/10 hover:bg-[#1C1B1A]/20 text-[#F5F2ED] rounded-md text-sm border border-[#A89360]/30 disabled:opacity-50"
                  data-testid="arena-share-btn-spectator"
                >
                  <Share size={14} /> {shareBusy ? "…" : "Share battle"}
                </button>
              </div>
            )}
            {is_spectator && (
              <p className="mt-4 text-[11px] text-[#6B6A68] flex items-center gap-1.5">
                <Lock size={11} /> Transcripts are reserved for the contestants.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] uppercase tracking-wider text-[#6B6A68] mb-1.5">{label}</label>
      {children}
    </div>
  );
}


function StateBadge({ state, countdown }) {
  const map = {
    pending_acceptance: { label: "Pending acceptance", color: "#A89360" },
    broadcast_open: { label: "Open challenge", color: "#A89360" },
    scheduled: { label: "Scheduled", color: "#A89360" },
    ready: { label: countdown !== null ? `Starting in ${countdown}s` : "Starting soon", color: "#C16E5A" },
    live: { label: "● Live", color: "#C16E5A" },
    finished: { label: "Finished", color: "#4A5D4E" },
    declined: { label: "Declined", color: "#6B6A68" },
    cancelled: { label: "Cancelled", color: "#6B6A68" },
    aborted: { label: "Aborted", color: "#C16E5A" },
  };
  const m = map[state] || { label: state, color: "#A89360" };
  return <span className="text-[11px] uppercase tracking-widest" style={{ color: m.color }} data-testid={`arena-state-${state}`}>{m.label}</span>;
}

function SiloBanner({ silo, won, delta, side }) {
  if (!silo) return <div className="bg-[#1C1B1A] border border-[#1C1B1A] rounded-lg p-4 text-center text-[#6B6A68]">Awaiting opponent…</div>;
  return (
    <div className={`bg-[#1C1B1A] rounded-lg p-4 ${won ? "border-2 border-[#A89360]" : "border border-[#1C1B1A]"} ${side === "right" ? "text-right" : ""}`}>
      <div className={`flex items-center gap-2 ${side === "right" ? "justify-end" : ""} mb-1`}>
        {won && <Trophy size={16} className="text-[#A89360]" />}
        <span className="text-base font-medium text-[#F5F2ED]">{silo.site_name}</span>
      </div>
      <div className="text-[11px] text-[#A89360] uppercase tracking-wider">{silo.tier} · Elo {silo.elo}</div>
      <div className="text-[11px] text-[#6B6A68] mt-0.5">
        {silo.record?.wins || 0}W – {silo.record?.losses || 0}L{(silo.record?.draws || 0) > 0 ? ` – ${silo.record.draws}D` : ""}
        {typeof delta === "number" && delta !== 0 && (
          <span className={delta > 0 ? "text-[#4A5D4E] ml-2" : "text-[#C16E5A] ml-2"}>
            ({delta > 0 ? `+${delta}` : delta})
          </span>
        )}
      </div>
    </div>
  );
}

function RoundCard({ round, index, l, r, opponentSide }) {
  const winnerSiloName = round.winner === "left" ? l?.site_name : round.winner === "right" ? r?.site_name : "Draw";
  return (
    <div className="bg-[#1C1B1A] border border-[#1C1B1A] rounded-lg p-5 arena-text">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-widest text-[#A89360]">Round {index + 1}</span>
        <span className="text-[11px] uppercase tracking-widest text-[#6B6A68]">
          Winner: <span className={round.winner === "draw" ? "text-[#A89360]" : "text-[#F5F2ED]"}>{winnerSiloName}</span>
        </span>
      </div>
      <p className="text-[#F5F2ED] mb-4 italic">"{round.question}"</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <AnswerPanel name={l?.site_name} answer={round.left_answer} scores={round.scores_left} total={round.left_total} winner={round.winner === "left"} isOpponent={opponentSide === "left"} />
        <AnswerPanel name={r?.site_name} answer={round.right_answer} scores={round.scores_right} total={round.right_total} winner={round.winner === "right"} isOpponent={opponentSide === "right"} />
      </div>

      <div className="border-t border-[#0F0E0D] pt-3">
        <div className="text-[11px] uppercase tracking-widest text-[#A89360] mb-1">Referee</div>
        <p className="text-sm text-[#F5F2ED] leading-relaxed">{round.verdict}</p>
      </div>
    </div>
  );
}

function AnswerPanel({ name, answer, scores, total, winner, isOpponent }) {
  // Feb 17, 2026 — `isOpponent=true` adds the opponent-locked class so a
  // CONTESTANT can't drag-copy their opponent's content (was previously
  // only gated for spectators via .spectator-locked on the outer wrapper).
  return (
    <div className={`rounded-md p-3 ${winner ? "bg-[#A89360]/10 border border-[#A89360]/40" : "bg-[#0F0E0D] border border-[#1C1B1A]"} ${isOpponent ? "opponent-locked" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[#A89360]">{name}</span>
        <span className="text-[11px] text-[#F5F2ED]">{total ?? "—"}/50</span>
      </div>
      <p
        className="text-sm text-[#F5F2ED] leading-relaxed whitespace-pre-line max-h-[260px] overflow-y-auto arena-text"
        onCopy={isOpponent ? (e) => { e.preventDefault(); } : undefined}
        onContextMenu={isOpponent ? (e) => { e.preventDefault(); } : undefined}
      >{answer}</p>
      {scores && Object.keys(scores).length > 0 && (
        <div className="mt-3 grid grid-cols-5 gap-1 text-center text-[10px]">
          {Object.entries(scores).map(([axis, val]) => (
            <div key={axis} className="bg-[#1C1B1A] rounded py-1">
              <div className="text-[#F5F2ED] font-medium">{val}</div>
              <div className="text-[#6B6A68] uppercase">{axis.slice(0, 3)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpectatorGateCard({ battle, onAdvance, advancing, autoAdvance, onToggleAutoAdvance }) {
  // 5-minute server-side auto-advance ceiling; show how long until it fires.
  const [secondsLeft, setSecondsLeft] = useState(null);
  useEffect(() => {
    if (!battle?.gated_round_started_at) return;
    const startedAt = new Date(battle.gated_round_started_at).getTime();
    const tick = () => {
      const elapsedMs = Date.now() - startedAt;
      const remaining = Math.max(0, Math.ceil((5 * 60 * 1000 - elapsedMs) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [battle?.gated_round_started_at]);
  const i = battle.gated_round_index ?? 0;
  const total = battle.question_count || (battle.rounds || []).length + 1;
  const mins = secondsLeft != null ? Math.floor(secondsLeft / 60) : null;
  const secs = secondsLeft != null ? secondsLeft % 60 : null;
  return (
    <div
      className="bg-gradient-to-br from-[#1C1B1A] to-[#0F0E0D] border-2 border-[#C16E5A]/40 rounded-lg p-6 text-center arena-text"
      data-testid="spectator-gate-card"
    >
      <div className="text-[11px] uppercase tracking-widest text-[#C16E5A] mb-2">Paused for reading · Round {i + 1} of {total}</div>
      <p className="text-sm text-[#F5F2ED] mb-4 max-w-md mx-auto leading-relaxed">
        Take a moment to read the previous round. When anyone watching is ready, hit the button below to fire the next round.
      </p>
      <button
        onClick={onAdvance}
        disabled={advancing}
        data-testid="spectator-advance-btn"
        className="text-base px-7 py-3 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] disabled:opacity-50 font-medium tracking-wide transition"
      >
        {advancing ? "Starting…" : `Start round ${i + 1}`}
      </button>
      {secondsLeft != null && secondsLeft > 0 && (
        <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
          Auto-advance in {mins}:{String(secs).padStart(2, "0")}
        </div>
      )}
      <button
        onClick={onToggleAutoAdvance}
        data-testid="spectator-auto-advance-toggle"
        className="mt-4 text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2 text-[#6B6A68] hover:text-[#F5F2ED]"
      >
        <span
          className={`inline-block w-7 h-3.5 rounded-full transition-colors relative ${autoAdvance ? "bg-[#4A5D4E]" : "bg-[#3a3938]"}`}
        >
          <span
            className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${autoAdvance ? "left-3.5" : "left-0.5"}`}
          />
        </span>
        Auto-advance ({autoAdvance ? "ON" : "OFF"})
      </button>
    </div>
  );
}

function RetryingCard({ reason }) {
  return (
    <div
      className="bg-[#1C1B1A] border border-[#A06800]/50 rounded-lg p-5 text-center arena-text"
      data-testid="round-retrying-card"
    >
      <div className="text-[11px] uppercase tracking-widest text-[#A06800] mb-2 inline-flex items-center gap-1.5">
        <Hourglass size={11} className="animate-spin" style={{ animationDuration: "2s" }} /> Retrying round…
      </div>
      <p className="text-sm text-[#F5F2ED] mb-1">A transient hiccup — we&apos;ll retry in 30 seconds.</p>
      {reason && <p className="text-[11px] text-[#6B6A68] italic max-w-md mx-auto mt-1">{reason}</p>}
    </div>
  );
}

function LiveRoundIndicator({ battle, l, r }) {
  const i = battle.current_round_index ?? 0;
  const total = battle.question_count || 3;
  const stage = battle.current_round_state || "thinking";
  const stageLabel = {
    thinking: `Both silos thinking…`,
    judging: `Referee deliberating…`,
    verdict_revealed: `Round ${i + 1} verdict in.`,
    finished: "Match complete.",
  }[stage] || stage;
  return (
    <div className="bg-[#1C1B1A] border border-[#A89360]/30 rounded-lg p-5 text-center arena-text">
      <div className="text-[11px] uppercase tracking-widest text-[#A89360] mb-2">Round {i + 1} of {total}</div>
      <div className="text-base text-[#F5F2ED] mb-1">{stageLabel}</div>
      {battle.current_question && stage !== "finished" && <p className="text-sm text-[#6B6A68] italic mt-2">"{battle.current_question}"</p>}
      <div className="mt-3 flex items-center justify-center gap-1">
        {[0, 1, 2].map(d => (
          <span key={d} className="w-2 h-2 rounded-full bg-[#A89360]/60 animate-pulse" style={{ animationDelay: `${d * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}
